/* eslint-disable react-hooks/exhaustive-deps */
import { Container, Spinner, Form, Alert, InputGroup } from "react-bootstrap";
import Navbar from "../../components/navbar";
import Layout from "../../layout";
import "./index.scss";
import { useBetween } from "use-between";
import { useAuthApi, useCatalogApi } from "../useApi";
import { useEffect, useState } from "react";
import AwareButton from "../../components/button";
import { Eye, CloseEye } from "../../components/icons";

const Password = () => {
  const {
    customer,
    customerId,
    getCustomer,
    updatePassword,
    logout
  } = useBetween(useAuthApi);
  const { updating, setUpdating } = useBetween(useCatalogApi);

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  
  // Password visibility states
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  useEffect(() => {
    setUpdating(true);
    getCustomer(customerId)
      .then(() => setUpdating(false))
      .catch(() => logout());
  }, [customerId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const errors = {};


    if (!formData.newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = "New password must be at least 8 characters long";
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm your new password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setShowErrorAlert(false);
    setShowSuccessAlert(false);

    try {
      await updatePassword(customerId, {
        newPassword: formData.newPassword
      });

      setShowSuccessAlert(true);
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      // Reset password visibility when form is cleared
      setShowPasswords({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false
      });
    } catch (error) {
      setShowErrorAlert(true);
      setErrorMessage(error?.message || "Failed to update password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearForm = () => {
    setFormData({
      newPassword: "",
      confirmPassword: ""
    });
    setShowPasswords({
      newPassword: false,
      confirmPassword: false
    });
    setValidationErrors({});
    setShowErrorAlert(false);
    setShowSuccessAlert(false);
  };

  return (
    <Layout>
      <div className="bg-gray">
        <Container className="password-container d-flex">
          <Navbar active="password" />
          <div className="password-wrapper">
            <h4>Update your password.</h4>

            {showSuccessAlert && (
              <Alert
                variant="success"
                dismissible
                onClose={() => setShowSuccessAlert(false)}
                className="mb-3"
              >
                Password updated successfully!
              </Alert>
            )}
            
            {showErrorAlert && (
              <Alert 
                variant="danger" 
                dismissible 
                onClose={() => setShowErrorAlert(false)}
                className="mb-3"
              >
                {errorMessage}
              </Alert>
            )}

            <Form onSubmit={handleSubmit} className="password-form">
              <Form.Group className="form-group">
                <Form.Label>New Password *</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPasswords.newPassword ? "text" : "password"}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.newPassword}
                    disabled={isSubmitting}
                    placeholder="Must be at least 8 characters"
                    className="password-input"
                  />
                  <InputGroup.Text 
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility('newPassword')}
                  >
                    {showPasswords.newPassword ? 
                      <CloseEye width="16" height="16" /> : 
                      <Eye width="16" height="16" />
                    }
                  </InputGroup.Text>
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.newPassword}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Confirm New Password *</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPasswords.confirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.confirmPassword}
                    disabled={isSubmitting}
                    className="password-input"
                  />
                  <InputGroup.Text 
                    className="password-toggle"
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                  >
                    {showPasswords.confirmPassword ? 
                      <CloseEye width="16" height="16" /> : 
                      <Eye width="16" height="16" />
                    }
                  </InputGroup.Text>
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.confirmPassword}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="submit-group mt-4">
                <AwareButton 
                  type="submit"
                  className="btn btn-primary primary me-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Password"}
                </AwareButton>
                <AwareButton 
                  type="button"
                  className="btn btn-secondary second"
                  onClick={clearForm}
                  disabled={isSubmitting}
                >
                  Clear
                </AwareButton>
              </div>
            </Form>

            {Object.keys(customer).length ? (
              <div className="contact-rep-section mt-5">
                <p className="help-text">Need help with your password? Contact your rep:</p>
                <div className="submit-group mt-3">
                  <AwareButton 
                    className="btn me-3 btn-secondary second" 
                    href={`tel:${customer["IA:Representative Phone"]}`}
                  >
                    Call Rep
                  </AwareButton>
                  <AwareButton 
                    className="btn btn-primary primary" 
                    href={`mailto:${customer["IA:Representative Email"]}`}
                  >
                    Email Rep
                  </AwareButton>
                </div>
              </div>
            ) : null}
          </div>
        </Container>
      </div>
      {updating ? (
        <div className="page-loading">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : null}
    </Layout>
  );
};

export default Password;
